import React, { useCallback, ChangeEvent } from "react";

// Component imports
import { CategoryDropdown, DisplayToggle, OrderDropdown, SearchField } from "@components";
import { Grid, Hidden } from "@material";

// Misc Imports
import { useTranslation } from "@i18n";
import { makeStyles } from "@styles";

const useStyles = makeStyles((theme: Theme) => ({
  stickyHeader: {
    position: "sticky",
    top: 72,
    backgroundColor: theme.palette.white,
    zIndex: 4,
    paddingTop: 0,
    paddingBottom: 16,
    marginTop: 0,
    [theme.breakpoints.down("lg")]: {
      top: 72,
      marginTop: 0,
    },
    [theme.breakpoints.down("md")]: {
      top: 64,
      marginTop: 0,
    },
    [theme.breakpoints.down("xs")]: {
      top: 56,
    },
  },
  mobileToggleContainer: {
    marginLeft: 8,
  },
  selectorsHeaderMedium: {
    paddingTop: 9,
  },
  searchBar: {
    width: 338,
    [theme.breakpoints.up(1100)]: {
      width: 438,
    },
  },
  categoriesHeader: {
    backgroundColor: theme.palette.white,
    zIndex: 5,
    paddingTop: 2,
  },
}));

type Props = {
  filters: OnlineScreenFilters;
  setFilters: any;
};

export const FiltersHeader = ({ filters, setFilters }: Props) => {
  const classes: any = useStyles({});
  const { t } = useTranslation("common");

  const handleSearchInput = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target as HTMLInputElement;
    setFilters((prevState) => ({
      ...prevState,
      searchString: value,
    }));
  }, []);

  const handleClearSearch = useCallback((): void => {
    setFilters((prevState) => ({
      ...prevState,
      searchString: "",
    }));
  }, []);

  return (
    <>
      {/* Mobile View */}
      <Hidden only={["sm", "md", "lg", "xl"]}>
        <Grid
          container
          item
          justifyContent="space-between"
          alignItems="center"
          xs={12}
          className={classes.stickyHeader}
        >
          <Grid item xs>
            <SearchField
              width={"100%"}
              value={filters.searchString}
              placeholder={t("searchOnlineStores")}
              onChange={(event) => handleSearchInput(event)}
              onClear={handleClearSearch}
            />
          </Grid>
          <Grid className={classes.mobileToggleContainer}>
            <DisplayToggle
              type="mobile"
              display={filters.display}
              setFilters={setFilters}
            />
          </Grid>
        </Grid>
        <div className={classes.categoriesHeader}>
          <Grid container item justifyContent="space-between" xs={12} sm>
            <Grid item xs={6} sm={6}>
              <CategoryDropdown category={filters.category} setFilters={setFilters} />
            </Grid>
            <Grid item xs={6} sm={6}>
              <OrderDropdown order={filters.order} setFilters={setFilters} />
            </Grid>
          </Grid>
        </div>
      </Hidden>
      {/* Tablet View */}
      <Hidden only={["xs", "md", "lg", "xl"]}>
        <Grid className={classes.stickyHeader}>
          <SearchField
            width={"100%"}
            value={filters.searchString}
            placeholder={t("searchOnlineStores")}
            onChange={(event) => handleSearchInput(event)}
            onClear={handleClearSearch}
          />
        </Grid>
        <Grid
          container
          item
          justifyContent="flex-end"
          alignItems="flex-start"
          className={classes.selectorsHeaderMedium}
        >
          <CategoryDropdown category={filters.category} setFilters={setFilters} />
          <OrderDropdown order={filters.order} setFilters={setFilters} />
          <DisplayToggle
            type="desktop"
            display={filters.display}
            setFilters={setFilters}
          />
        </Grid>
      </Hidden>
      {/* Desktop View */}
      <Hidden only={["xs", "sm"]}>
        <Grid container justifyContent="space-between" className={classes.stickyHeader}>
          <Grid className={classes.searchBar}>
            <SearchField
              width={"100%"}
              value={filters.searchString}
              placeholder={t("searchOnlineStores")}
              onChange={(event) => handleSearchInput(event)}
              onClear={handleClearSearch}
            />
          </Grid>
          <Grid container item justifyContent="flex-end" alignItems="flex-start" md>
            <CategoryDropdown category={filters.category} setFilters={setFilters} />
            <OrderDropdown order={filters.order} setFilters={setFilters} />
            <DisplayToggle
              type="desktop"
              display={filters.display}
              setFilters={setFilters}
            />
          </Grid>
        </Grid>
      </Hidden>
    </>
  );
};
